import React, { useState, useEffect } from 'react';
import { Modal, Card, Row, Col, Table, Button } from 'react-bootstrap';
import { invoiceService } from '../../services/api';
import { Invoice } from '../../types';
import { toast } from 'react-toastify';
import StatusBadge from '../common/StatusBadge';

interface InvoiceDetailsProps {
  show: boolean;
  onHide: () => void;
  invoiceId: number | null;
  onUpdate: () => void;
}

const InvoiceDetails: React.FC<InvoiceDetailsProps> = ({ show, onHide, invoiceId, onUpdate }) => {
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (show && invoiceId) {
      fetchInvoice();
    }
  }, [show, invoiceId]);

  const fetchInvoice = async () => {
    if (!invoiceId) return;
    
    try {
      setLoading(true);
      const response = await invoiceService.getById(invoiceId);
      setInvoice(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement de la facture');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPdf = async () => {
    if (!invoice) return;

    try {
      const response = await invoiceService.generatePdf(invoice.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `facture-${invoice.invoice_number}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erreur lors de l\'export PDF');
    }
  };

  const handleExportExcel = async () => {
    if (!invoice) return;

    try {
      const response = await invoiceService.generateExcel(invoice.id);
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `facture-${invoice.invoice_number}.xlsx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erreur lors de l\'export Excel');
    }
  };

  const getTypeBadge = (type: string) => {
    const typeMap = {
      sale: { bg: 'success', text: 'Vente' },
      purchase: { bg: 'info', text: 'Achat' },
      credit_note: { bg: 'warning', text: 'Avoir' },
    };
    const config = typeMap[type as keyof typeof typeMap] || { bg: 'secondary', text: type };
    return <span className={`badge bg-${config.bg}`}>{config.text}</span>;
  };

  if (loading) {
    return (
      <Modal show={show} onHide={onHide} size="xl">
        <Modal.Body className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Chargement...</span>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  if (!invoice) return null;

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Détails de la facture {invoice.invoice_number}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Row className="mb-4">
          <Col md={6}>
            <Card>
              <Card.Header>
                <h6 className="mb-0">Informations générales</h6>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col sm={4}><strong>Numéro:</strong></Col>
                  <Col sm={8}>{invoice.invoice_number}</Col>
                </Row>
                <Row>
                  <Col sm={4}><strong>Type:</strong></Col>
                  <Col sm={8}>{getTypeBadge(invoice.type)}</Col>
                </Row>
                <Row>
                  <Col sm={4}><strong>{invoice.type === 'sale' ? 'Client:' : 'Fournisseur:'}</strong></Col>
                  <Col sm={8}>{invoice.client?.name || invoice.supplier?.name}</Col>
                </Row>
                <Row>
                  <Col sm={4}><strong>Date:</strong></Col>
                  <Col sm={8}>{new Date(invoice.date).toLocaleDateString('fr-FR')}</Col>
                </Row>
                <Row>
                  <Col sm={4}><strong>Échéance:</strong></Col>
                  <Col sm={8}>{new Date(invoice.due_date).toLocaleDateString('fr-FR')}</Col>
                </Row>
                <Row>
                  <Col sm={4}><strong>Statut:</strong></Col>
                  <Col sm={8}><StatusBadge status={invoice.status} type="invoice" /></Col>
                </Row>
                {invoice.quote && (
                  <Row>
                    <Col sm={4}><strong>Devis:</strong></Col>
                    <Col sm={8}>{invoice.quote.quote_number}</Col>
                  </Row>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card>
              <Card.Header>
                <h6 className="mb-0">Montants</h6>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col sm={6}><strong>Sous-total HT:</strong></Col>
                  <Col sm={6} className="text-end">
                    {new Intl.NumberFormat('fr-FR', {
                      style: 'currency',
                      currency: 'MAD'
                    }).format(invoice.subtotal_ht)}
                  </Col>
                </Row>
                <Row>
                  <Col sm={6}><strong>TVA:</strong></Col>
                  <Col sm={6} className="text-end">
                    {new Intl.NumberFormat('fr-FR', {
                      style: 'currency',
                      currency: 'MAD'
                    }).format(invoice.total_vat)}
                  </Col>
                </Row>
                <Row className="border-top pt-2">
                  <Col sm={6}><strong>Total TTC:</strong></Col>
                  <Col sm={6} className="text-end">
                    <strong>
                      {new Intl.NumberFormat('fr-FR', {
                        style: 'currency',
                        currency: 'MAD'
                      }).format(invoice.total_ttc)}
                    </strong>
                  </Col>
                </Row>
                <Row>
                  <Col sm={6}><strong>Montant payé:</strong></Col>
                  <Col sm={6} className="text-end">
                    {new Intl.NumberFormat('fr-FR', {
                      style: 'currency',
                      currency: 'MAD'
                    }).format(invoice.paid_amount)}
                  </Col>
                </Row>
                <Row>
                  <Col sm={6}><strong>Reste à payer:</strong></Col>
                  <Col sm={6} className="text-end">
                    <strong className={invoice.remaining_amount > 0 ? 'text-danger' : 'text-success'}>
                      {new Intl.NumberFormat('fr-FR', {
                        style: 'currency',
                        currency: 'MAD'
                      }).format(invoice.remaining_amount)}
                    </strong>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="mb-4">
          <Card.Header>
            <h6 className="mb-0">Articles</h6>
          </Card.Header>
          <Card.Body>
            <Table striped bordered hover>
              <thead>
                <tr>
                  <th>Produit/Service</th>
                  <th>Description</th>
                  <th>Quantité</th>
                  <th>Prix unitaire</th>
                  <th>TVA</th>
                  <th>Total HT</th>
                  <th>Total TTC</th>
                </tr>
              </thead>
              <tbody>
                {invoice.items?.map((item, index) => (
                  <tr key={index}>
                    <td>{item.product_name}</td>
                    <td>{item.description}</td>
                    <td>{item.quantity} {item.unit}</td>
                    <td>
                      {new Intl.NumberFormat('fr-FR', {
                        style: 'currency',
                        currency: 'MAD'
                      }).format(item.unit_price)}
                    </td>
                    <td>{item.vat_rate}%</td>
                    <td>
                      {new Intl.NumberFormat('fr-FR', {
                        style: 'currency',
                        currency: 'MAD'
                      }).format(item.total_ht)}
                    </td>
                    <td>
                      {new Intl.NumberFormat('fr-FR', {
                        style: 'currency',
                        currency: 'MAD'
                      }).format(item.total_ttc)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>

        {(invoice.notes || invoice.terms) && (
          <Row>
            {invoice.notes && (
              <Col md={6}>
                <Card>
                  <Card.Header>
                    <h6 className="mb-0">Notes</h6>
                  </Card.Header>
                  <Card.Body>
                    <p className="mb-0">{invoice.notes}</p>
                  </Card.Body>
                </Card>
              </Col>
            )}
            {invoice.terms && (
              <Col md={6}>
                <Card>
                  <Card.Header>
                    <h6 className="mb-0">Conditions</h6>
                  </Card.Header>
                  <Card.Body>
                    <p className="mb-0">{invoice.terms}</p>
                  </Card.Body>
                </Card>
              </Col>
            )}
          </Row>
        )}
      </Modal.Body>
      <Modal.Footer>
        <div className="d-flex justify-content-between w-100">
          <div>
            {/* Actions spécifiques selon le statut */}
          </div>
          <div>
            <Button variant="outline-primary" onClick={handleExportPdf} className="me-2">
              Télécharger PDF
            </Button>
            <Button variant="outline-success" onClick={handleExportExcel} className="me-2">
              Télécharger Excel
            </Button>
            <Button variant="secondary" onClick={onHide}>
              Fermer
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default InvoiceDetails;